import type { Completion, CompletionMode } from "@canvaschool/contracts";

/** Ten rows is the tallest that still fits beside the canvas without scrolling. */
const MAX_ROWS = 10;

export const stripColumns = (
  mode: CompletionMode,
  slotCount: number,
): number => {
  if (mode === "GROUP") {
    return 1;
  }
  return Math.min(3, Math.max(2, Math.ceil(slotCount / MAX_ROWS)));
};

export type CompletionStripProps = {
  completion: Completion;
  /** The numbers to draw, in order: owned frames (INDIVIDUAL) or 1..groupCount. */
  slots: readonly number[];
  onToggle(slot: number, checked: boolean): void;
  /** Every slot is checked; the caller adds a screen-level highlight even
   * when the chime itself is muted or blocked by the browser. */
  allChecked?: boolean;
};

export const CompletionStrip = ({
  completion,
  slots,
  onToggle,
  allChecked = false,
}: CompletionStripProps) => {
  const columns = stripColumns(completion.mode, slots.length);
  const suffix = completion.mode === "GROUP" ? "모둠" : "번";
  // GROUP mode is a single full-width column of few, large buttons; stretch
  // them edge-to-edge rather than leaving the rest of the row empty at the
  // slot's fixed small-button width (INDIVIDUAL mode keeps that width).
  const classNames = [
    "canvaschool-completion",
    completion.mode === "GROUP" ? "canvaschool-completion--group" : null,
    allChecked ? "canvaschool-completion--all" : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classNames}
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      role="group"
      aria-label="완료 표시"
    >
      {slots.map((slot) => {
        const checked = completion.checked.includes(slot);
        return (
          <button
            key={slot}
            type="button"
            aria-pressed={checked}
            aria-label={`${slot}${suffix} 완료${checked ? " 취소" : ""}`}
            className={
              checked
                ? "canvaschool-completion-slot canvaschool-completion-slot--done"
                : "canvaschool-completion-slot"
            }
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(slot, !checked);
            }}
          >
            {slot}
          </button>
        );
      })}
    </div>
  );
};
